import { useEffect, useRef, useState } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
} from "recharts";
import { getOrgSpdTemplate } from "../service/service_handler";
import { computeBandPercentages } from "./spdBandUtils";
import { buildPlanckianLocus, computeCCTFromSPD } from "../../utils/cct";

const buildChartDataAndSpdMix = (template, spectrum, locusTable) => {
  const wlValues = Array.isArray(template?.wl) ? template.wl : [];
  const channels = template?.channels || {};
  const powerPct = template?.powerPct || {};

  if (!wlValues.length) {
    return { chartData: [], spdMix: [], bandPercentages: null, cctResult: { CCT: null, Duv: null } };
  }

  const spectrumByName = new Map(
    (spectrum || []).map((item) => [item.channelName, item])
  );

  const spdMix = new Array(wlValues.length).fill(0);

  Object.keys(channels).forEach((channelName) => {
    const channelData = channels[channelName] || [];
    const slider = spectrumByName.get(channelName);
    const sliderValue =
      slider && slider.isActive ? Number(slider.channelValue) : 0;
    const powerWeight = Number(powerPct[channelName]) || 0;
    const weight = powerWeight * (sliderValue / 100);

    if (!weight) {
      return;
    }

    for (let i = 0; i < wlValues.length; i += 1) {
      const value = Number(channelData[i]) || 0;
      spdMix[i] += value * weight;
    }
  });

  const maxValue = Math.max(0, ...spdMix);

  const chartData = wlValues.map((wlValue, index) => ({
    wl: Number(wlValue),
    y: maxValue > 0 ? spdMix[index] / maxValue : spdMix[index],
  }));

  const bandPercentages = computeBandPercentages(template, spdMix);
  const cctResult = computeCCTFromSPD(template, spdMix, locusTable);

  return { chartData, spdMix, bandPercentages, cctResult };
};

/** Band colors aligned with SPD graph gradient and slider theme (UV, Blue, Green, Red, Far-Red) */
const BAND_BAR_COLORS = [
  "#8B5CF6", /* UV */
  "#3B82F6", /* Blue */
  "#22C55E", /* Green */
  "#EF4444", /* Red */
  "#7F1D1D", /* Far-Red */
];

const defaultGradientStops = [
  { offset: 0, color: "#1a1a1a" },
  { offset: 5, color: "#8B5CF6" },
  { offset: 15, color: "#3B82F6" },
  { offset: 30, color: "#3B82F6" },
  { offset: 42, color: "#22C55E" },
  { offset: 52, color: "#FACC15" },
  { offset: 62, color: "#EF4444" },
  { offset: 75, color: "#EF4444" },
  { offset: 85, color: "#EC4899" },
  { offset: 100, color: "#7F1D1D" },
];

const buildGradientStops = (template) => {
  const wlValues = Array.isArray(template?.wl) ? template.wl : [];
  if (!wlValues.length) {
    return defaultGradientStops;
  }
  const min = wlValues.reduce((acc, value) => Math.min(acc, value), wlValues[0]);
  const max = wlValues.reduce((acc, value) => Math.max(acc, value), wlValues[0]);
  if (!Number.isFinite(min) || !Number.isFinite(max) || min === max) {
    return defaultGradientStops;
  }
  const anchors = [
    { wl: 380, color: "#8B5CF6" },
    { wl: 450, color: "#3B82F6" },
    { wl: 495, color: "#3B82F6" },
    { wl: 520, color: "#22C55E" },
    { wl: 560, color: "#FACC15" },
    { wl: 620, color: "#EF4444" },
    { wl: 660, color: "#EF4444" },
    { wl: 700, color: "#EC4899" },
    { wl: 750, color: "#7F1D1D" },
  ];
  return [
    { offset: 0, color: "#1a1a1a" },
    ...anchors.map((anchor) => {
      const offset =
        ((anchor.wl - min) / (max - min)) * 100;
      const clamped = Math.min(100, Math.max(0, offset));
      return { offset: clamped, color: anchor.color };
    }),
  ];
};

const buildXAxisTicks = (template, step = 25) => {
  const wlValues = Array.isArray(template?.wl) ? template.wl : [];
  if (!wlValues.length) {
    return [];
  }
  const min = wlValues.reduce((acc, value) => Math.min(acc, value), wlValues[0]);
  const max = wlValues.reduce((acc, value) => Math.max(acc, value), wlValues[0]);
  if (!Number.isFinite(min) || !Number.isFinite(max) || min === max) {
    return [];
  }
  const start = Math.ceil(min / step) * step;
  const ticks = [];
  for (let value = start; value <= max; value += step) {
    ticks.push(value);
  }
  return ticks;
};

const formatTooltipValue = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return value;
  }
  return numeric.toFixed(2);
};

export default function SpdGraph({ spectrum, height = 260 }) {
  const templateRef = useRef(null);
  const locusRef = useRef(null);
  const debounceRef = useRef(null);
  const spectrumRef = useRef(spectrum);
  const [chartData, setChartData] = useState([]);
  const [bandPercentages, setBandPercentages] = useState(null);
  const [cctResult, setCctResult] = useState({ CCT: null, Duv: null });
  const [loading, setLoading] = useState(true);
  const [notConfigured, setNotConfigured] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [gradientStops, setGradientStops] = useState(defaultGradientStops);
  const [xTicks, setXTicks] = useState([]);

  useEffect(() => {
    spectrumRef.current = spectrum;
  }, [spectrum]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setNotConfigured(false);
    setErrorMsg("");

    getOrgSpdTemplate()
      .then((response) => {
        if (!mounted) {
          return;
        }
        templateRef.current = response.data;
        locusRef.current = buildPlanckianLocus(response.data);
        setLoading(false);
        setGradientStops(buildGradientStops(response.data));
        setXTicks(buildXAxisTicks(response.data, 25));
        const { chartData: cd, bandPercentages: bp, cctResult: cct } = buildChartDataAndSpdMix(response.data, spectrumRef.current, locusRef.current);
        setChartData(cd);
        setBandPercentages(bp);
        setCctResult(cct);
        if (process.env.NODE_ENV !== "production" && cct?.CCT != null) {
          console.log("CCT (debug):", cct.CCT, "K", "Duv:", cct.Duv);
        }
      })
      .catch((error) => {
        if (!mounted) {
          return;
        }
        if (
          error?.response?.status === 404 ||
          error?.response?.data?.code === "SPD_NOT_CONFIGURED"
        ) {
          setNotConfigured(true);
        } else {
          setErrorMsg("Unable to load SPD template");
        }
        setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!templateRef.current) {
      return undefined;
    }
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      const { chartData: cd, bandPercentages: bp, cctResult: cct } = buildChartDataAndSpdMix(templateRef.current, spectrum, locusRef.current);
      setChartData(cd);
      setBandPercentages(bp);
      setCctResult(cct);
      if (process.env.NODE_ENV !== "production" && cct?.CCT != null) {
        console.log("CCT (debug):", cct.CCT, "K", "Duv:", cct.Duv);
      }
    }, 120);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [spectrum]);

  return (
    <Box sx={{ mt: 0 }}>
      <Typography
        variant="subtitle2"
        color="text.secondary"
        sx={{ mb: 0.5, mt: 0, textAlign: "left", fontWeight: 400, fontSize: "1.25rem" }}
      >
        Spectrum
      </Typography>
      <Paper elevation={1} sx={{ p: 1, pl: 0, pb: 0.25 }}>
        {loading && (
          <Typography variant="body2">Loading SPD template...</Typography>
        )}
        {!loading && notConfigured && (
          <Typography variant="body2">
            SPD not configured for your organization
          </Typography>
        )}
        {!loading && !notConfigured && errorMsg && (
          <Typography variant="body2">{errorMsg}</Typography>
        )}
        {!loading && !notConfigured && !errorMsg && (
          <ResponsiveContainer width="100%" height={height}>
            <AreaChart
              data={chartData}
              margin={{ top: 2, right: 8, left: -18, bottom: 8 }}
            >
              <defs>
                <linearGradient id="spdSpectrum" x1="0" y1="0" x2="1" y2="0">
                  {gradientStops.map((stop) => (
                    <stop
                      key={`${stop.offset}-${stop.color}`}
                      offset={`${stop.offset}%`}
                      stopColor={stop.color}
                      stopOpacity={1}
                    />
                  ))}
                </linearGradient>
              </defs>
              <XAxis
                dataKey="wl"
                ticks={xTicks}
                tickFormatter={(value) => Math.round(value)}
                tick={{ fontSize: 11 }}
                height={24}
              />
              <YAxis
                domain={[0, 1]}
                width={60}
                tickMargin={4}
                tick={{ fontSize: 11 }}
              />
              <Tooltip
                formatter={(value) => [formatTooltipValue(value), "Intensity"]}
              />
              <Area
                type="monotone"
                dataKey="y"
                stroke="#1976d2"
                fill="url(#spdSpectrum)"
                fillOpacity={1}
                dot={false}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
        {!loading && !notConfigured && !errorMsg && (
          <Box sx={{ mt: 0.25, pt: 0.5, borderTop: 1, borderColor: "divider" }}>
            <Stack direction="row" alignItems="flex-start" spacing={0} sx={{ width: "100%", minWidth: 0 }}>
              <Box sx={{ flex: "0 0 52%", minWidth: 0, pr: 1, pl: 1.5 }}>
                <Typography variant="body2" color="text.secondary" sx={{ display: "block", mb: 0.25 }} title={cctResult.CCT == null ? "Re-upload SPD template with xbar, ybar, zbar columns (CIE 1931 CMF) to show CCT" : ""}>
                  CCT: <Box component="span" sx={{ fontWeight: 700 }}>{cctResult.CCT != null ? `${cctResult.CCT} K` : "—"}</Box>
                  {cctResult.Duv != null && Math.abs(cctResult.Duv) > 0.005 && (
                    <> · <Box component="span" sx={{ fontSize: "60%", fontStyle: "italic" }}>(Tinted: Approx CCT)</Box></>
                  )}
                </Typography>
                <Stack direction="column" spacing={0.125}>
                  <Stack direction="row" spacing={1.5}>
                    <Typography variant="body2" component="span">
                      UV: <Box component="span" sx={{ fontWeight: 700, color: "#8B5CF6" }}>{bandPercentages ? `${bandPercentages.uvPct.toFixed(0)}%` : "—"}</Box>
                    </Typography>
                    <Typography variant="body2" component="span">
                      Blue: <Box component="span" sx={{ fontWeight: 700, color: "#3B82F6" }}>{bandPercentages ? `${bandPercentages.bluePct.toFixed(0)}%` : "—"}</Box>
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1.5}>
                    <Typography variant="body2" component="span">
                      Green: <Box component="span" sx={{ fontWeight: 700, color: "#22C55E" }}>{bandPercentages ? `${bandPercentages.greenPct.toFixed(0)}%` : "—"}</Box>
                    </Typography>
                    <Typography variant="body2" component="span">
                      Red: <Box component="span" sx={{ fontWeight: 700, color: "#EF4444" }}>{bandPercentages ? `${bandPercentages.redPct.toFixed(0)}%` : "—"}</Box>
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1.5}>
                    <Typography variant="body2" component="span">
                      Far-Red: <Box component="span" sx={{ fontWeight: 700, color: "#7F1D1D" }}>{bandPercentages ? `${bandPercentages.farRedPct.toFixed(0)}%` : "—"}</Box>
                    </Typography>
                  </Stack>
                </Stack>
              </Box>
              {bandPercentages && (
                <Box sx={{ flex: "0 0 48%", minWidth: 0, height: 120 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={[
                        { name: "UV", value: bandPercentages.uvPct },
                        { name: "Blue", value: bandPercentages.bluePct },
                        { name: "Green", value: bandPercentages.greenPct },
                        { name: "Red", value: bandPercentages.redPct },
                        { name: "Far-Red", value: bandPercentages.farRedPct },
                      ]}
                      margin={{ top: 4, right: 4, left: 4, bottom: 4 }}
                    >
                      <XAxis dataKey="name" tick={{ fontSize: 9 }} tickFormatter={(v) => (v === "Far-Red" ? "FR" : v)} />
                      <YAxis type="number" domain={[0, 100]} ticks={[50, 100]} tickFormatter={(v) => `${v}%`} width={28} tick={{ fontSize: 9 }} />
                      <Tooltip
                        content={({ active, payload }) => (active && payload?.length ? (
                          <Box component="span" sx={{ display: "block", fontSize: 9, px: 0.25, py: 0.125, backgroundColor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: 0.5, boxShadow: 1 }}>
                            {Number(payload[0].value).toFixed(0)}%
                          </Box>
                        ) : null)}
                        cursor={{ fill: "rgba(0,0,0,0.04)" }}
                      />
                      <Bar dataKey="value" radius={[2, 2, 0, 0]} maxBarSize={999}>
                        {[0, 1, 2, 3, 4].map((index) => (
                          <Cell key={`cell-${index}`} fill={BAND_BAR_COLORS[index]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </Stack>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
